import {
  ApiErrorResponseSchema,
  type ApiErrorCode,
  type CheckoutRequest,
  type CheckoutResponse,
  CheckoutResponseSchema,
  type ConfigResponseCompat,
  ConfigResponseCompatSchema,
  type DecodePlateRequest,
  type DecodePlateResponse,
  DecodePlateResponseSchema,
  type OutreachSeedResponse,
  OutreachSeedResponseSchema,
  type RateRequest,
  type RateResponse,
  RateResponseSchema,
} from 'shared';

export type WidgetApiErrorCode = ApiErrorCode | 'NETWORK' | 'UNKNOWN' | 'INVALID_RESPONSE';

export class ApiError extends Error {
  constructor(
    public readonly code: WidgetApiErrorCode,
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClient {
  config(): Promise<ConfigResponseCompat>;
  decodePlate(input: DecodePlateRequest): Promise<DecodePlateResponse>;
  rate(input: RateRequest): Promise<RateResponse>;
  checkout(input: CheckoutRequest): Promise<CheckoutResponse>;
  outreachOpen(outreachId: string): Promise<OutreachSeedResponse>;
}

export interface ApiClientOptions {
  apiBase: string;
  apiKey: string;
}

interface ResponseSchema<T> {
  safeParse(value: unknown): { success: true; data: T } | { success: false };
}

interface RequestOptions<T> {
  method: 'GET' | 'POST';
  path: string;
  schema: ResponseSchema<T>;
  body?: unknown;
  /** Non-2xx statuses to treat as success (today only `/api/rate` 503). */
  successStatuses?: number[];
}

export function createApiClient({ apiBase, apiKey }: ApiClientOptions): ApiClient {
  const base = apiBase.replace(/\/$/, '');

  async function request<T>({
    method,
    path,
    schema,
    body,
    successStatuses,
  }: RequestOptions<T>): Promise<T> {
    const headers: Record<string, string> = { 'account-api-key': apiKey };
    if (body !== undefined) headers['content-type'] = 'application/json';

    let response: Response;
    try {
      response = await fetch(`${base}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (err) {
      throw new ApiError(
        'NETWORK',
        0,
        err instanceof Error ? err.message : 'Network request failed',
      );
    }

    let parsed: unknown;
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }

    const isSuccess = response.ok || (successStatuses?.includes(response.status) ?? false);
    if (!isSuccess) {
      const envelope = ApiErrorResponseSchema.safeParse(parsed);
      throw new ApiError(
        envelope.success ? envelope.data.error : 'UNKNOWN',
        response.status,
        envelope.success ? envelope.data.message : `Request failed (HTTP ${response.status})`,
      );
    }

    const result = schema.safeParse(parsed);
    if (!result.success) {
      throw new ApiError(
        'INVALID_RESPONSE',
        response.status,
        'Server response did not match the expected schema',
      );
    }
    return result.data;
  }

  return {
    config: () =>
      request({
        method: 'GET',
        path: '/api/config',
        schema: ConfigResponseCompatSchema,
      }),
    decodePlate: (input) =>
      request({
        method: 'POST',
        path: '/api/decode-plate',
        schema: DecodePlateResponseSchema,
        body: input,
      }),
    rate: (input) =>
      request({
        method: 'POST',
        path: '/api/rate',
        schema: RateResponseSchema,
        body: input,
        successStatuses: [503],
      }),
    checkout: (input) =>
      request({
        method: 'POST',
        path: '/api/checkout',
        schema: CheckoutResponseSchema,
        body: input,
      }),
    outreachOpen: (outreachId) =>
      request({
        method: 'POST',
        path: `/api/outreach/${encodeURIComponent(outreachId)}`,
        schema: OutreachSeedResponseSchema,
      }),
  };
}

let _instance: ApiClient | null = null;

export function setApiClient(client: ApiClient): void {
  _instance = client;
}

export function getApiClient(): ApiClient {
  if (_instance === null) {
    throw new Error(
      '[sell-more] API client not initialized — App must mount before screens read it.',
    );
  }
  return _instance;
}
